import {FormInstance} from 'antd';
import {useMemo} from 'react';

import {ReviewsAppListType, useReviewsAppList} from '../../../../../service/reviews/reviews-apps';
import {ReviewCatalogType} from '../../../../../service/reviews/reviews-catalogs';
import {ReviewsStateType} from '../../reviews-state/reviews-state-type';

import {trackAnalytics} from './reviews-filter-helper';
import {ReviewsFilterDataType, ReviewsFilterFieldNameEnum} from './reviews-filter-type';

function isStoreSourceSelected(sourceList: Array<number>, storeCatalogIds: Array<number>): boolean {
    return sourceList.length === 0 || sourceList.some((catalogId) => storeCatalogIds.includes(catalogId));
}

type UseReviewsAppFilterOptionsType = {
    activeFilterStateForUi: ReviewsFilterDataType;
    form: FormInstance;
    onFieldsChange: () => void;
    reviewsCatalogList: Array<ReviewCatalogType> | null | undefined;
    reviewsState: ReviewsStateType;
};

type UseReviewsAppFilterResultType = {
    handleLocationIdsChange: (value: Array<string>) => void;
    handleSourceListChangeWithAppReset: (value: Array<number>) => void;
    reviewsAppList: ReviewsAppListType | undefined;
    reviewsAppListIsInProgress: boolean;
    showAppFilter: boolean;
    storeCatalogIds: Array<number>;
};

export function useReviewsAppFilter(options: UseReviewsAppFilterOptionsType): UseReviewsAppFilterResultType {
    const {activeFilterStateForUi, form, onFieldsChange, reviewsCatalogList, reviewsState} = options;

    const storeCatalogIds = useMemo(
        () => reviewsCatalogList?.filter((catalog) => catalog.is_store).map((catalog) => catalog.id) ?? [],
        [reviewsCatalogList]
    );
    const hasStoreCatalog = storeCatalogIds.length > 0;

    const reviewsStateForApps = useMemo(
        () => ({
            ...reviewsState,
            filterState: activeFilterStateForUi,
        }),
        [activeFilterStateForUi, reviewsState]
    );

    const isStoreSourceSelectedForUi = useMemo(
        () => isStoreSourceSelected(activeFilterStateForUi[ReviewsFilterFieldNameEnum.sourceList], storeCatalogIds),
        [activeFilterStateForUi, storeCatalogIds]
    );

    const {data: reviewsAppList, isFetching: reviewsAppListIsInProgress} = useReviewsAppList(
        reviewsStateForApps,
        storeCatalogIds,
        hasStoreCatalog && isStoreSourceSelectedForUi
    );

    const hasApps = Boolean(reviewsAppList?.results && reviewsAppList.results.length > 0);
    const showAppFilter = hasStoreCatalog && isStoreSourceSelectedForUi && hasApps;

    function handleSourceListChangeWithAppReset(value: Array<number>) {
        const nextIsStoreSourceSelected = isStoreSourceSelected(value, storeCatalogIds);

        if (nextIsStoreSourceSelected) {
            form.setFieldsValue({[ReviewsFilterFieldNameEnum.sourceList]: value});
        } else {
            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.sourceList]: value,
                [ReviewsFilterFieldNameEnum.locationIds]: [],
            });
        }

        onFieldsChange();
    }

    function handleLocationIdsChange(value: Array<string>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.locationIds]: value});
        onFieldsChange();

        trackAnalytics(ReviewsFilterFieldNameEnum.locationIds, value);
    }

    return {
        handleLocationIdsChange,
        handleSourceListChangeWithAppReset,
        reviewsAppList,
        reviewsAppListIsInProgress,
        showAppFilter,
        storeCatalogIds,
    };
}
